import React, { useState } from 'react';
import './Settings.css';

export default function Settings({ auth }) {
  const fullName = auth?.fullName || auth?.username || 'User';
  const username = auth?.username || 'user';
  const email = auth?.email || `${username}@scaalable.com`;
  const phone = auth?.phone || 'Not configured';
  const role = auth?.role || 'USER';
  const initial = (fullName[0] || 'U').toUpperCase();

  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [passwordMsg, setPasswordMsg] = useState('');

  const getRoleBadgeClass = (r) => {
    const roleLower = String(r || '').toLowerCase();
    return `badge badge-${roleLower}`;
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordForm.new !== passwordForm.confirm) {
      setPasswordMsg('New passwords do not match!');
      return;
    }
    // Mock API Call
    setTimeout(() => {
      setPasswordMsg('Password updated successfully!');
      setPasswordForm({ current: '', new: '', confirm: '' });
    }, 500);
  };

  return (
    <div className="settings-page">
      {/* Header */}
      <div className="settings-header">
        <div className="page-eyebrow">Account &amp; System Settings</div>
        <h1>Settings</h1>
        <p>Manage your account profile, role preferences, security, and workspace configuration.</p>
      </div>

      <div className="settings-grid">
        {/* Profile Overview Card */}
        <div className="settings-card">
          <div className="profile-hero">
            <div className="profile-avatar-xl">{initial}</div>
            <div className="profile-hero-info">
              <h3>{fullName}</h3>
              <div className="profile-hero-handle">@{username}</div>
              <div className="profile-hero-tags">
                <span className={getRoleBadgeClass(role)}>{role}</span>
                <span className="badge badge-active">
                  <span className="status-dot"></span> ACTIVE ACCOUNT
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Dynamic Role Specific Information */}
        {role === 'AGENT' && (
          <div className="settings-card">
            <div className="settings-card-header">
              <div className="settings-card-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                </svg>
              </div>
              <div>
                <h2>Agent Performance &amp; Information</h2>
                <p>Your current operational metrics and assignments within the workspace.</p>
              </div>
            </div>
            <div className="settings-fields-grid">
              <div className="settings-field">
                <label>Agent Tier Level</label>
                <div className="readonly-input-wrapper">
                  <input type="text" value="Tier 2 (Senior)" readOnly />
                  <span className="readonly-badge">Info</span>
                </div>
              </div>
              <div className="settings-field">
                <label>Assigned Campaign</label>
                <div className="readonly-input-wrapper">
                  <input type="text" value="Outbound Sales 2026" readOnly />
                  <span className="readonly-badge">Info</span>
                </div>
              </div>
              <div className="settings-field">
                <label>Total Calls Logged</label>
                <div className="readonly-input-wrapper">
                  <input type="text" value="1,245" readOnly />
                  <span className="readonly-badge">Stats</span>
                </div>
              </div>
              <div className="settings-field">
                <label>Performance Rating</label>
                <div className="readonly-input-wrapper">
                  <input type="text" value="98.4% (Excellent)" readOnly />
                  <span className="readonly-badge">Stats</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {role === 'MANAGER' && (
          <div className="settings-card">
            <div className="settings-card-header">
              <div className="settings-card-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div>
                <h2>Manager Workspace Information</h2>
                <p>Overview of your departmental allocations and team structures.</p>
              </div>
            </div>
            <div className="settings-fields-grid">
              <div className="settings-field">
                <label>Managed Department</label>
                <div className="readonly-input-wrapper">
                  <input type="text" value="Inside Sales Team A" readOnly />
                  <span className="readonly-badge">Info</span>
                </div>
              </div>
              <div className="settings-field">
                <label>Agents Supervised</label>
                <div className="readonly-input-wrapper">
                  <input type="text" value="14 Active Agents" readOnly />
                  <span className="readonly-badge">Stats</span>
                </div>
              </div>
              <div className="settings-field">
                <label>Monthly Target KPI</label>
                <div className="readonly-input-wrapper">
                  <input type="text" value="12,000 Calls" readOnly />
                  <span className="readonly-badge">Target</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Profile Information Section */}
        <div className="settings-card">
          <div className="settings-card-header">
            <div className="settings-card-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div>
              <h2>Profile &amp; Personal Information</h2>
              <p>Your current user identity registered in the CRM workspace.</p>
            </div>
          </div>

          <div className="settings-fields-grid">
            <div className="settings-field">
              <label>Full Name</label>
              <div className="readonly-input-wrapper">
                <input type="text" value={fullName} readOnly />
                <span className="readonly-badge">Read-only</span>
              </div>
            </div>

            <div className="settings-field">
              <label>Username</label>
              <div className="readonly-input-wrapper">
                <input type="text" value={username} readOnly />
                <span className="readonly-badge">Read-only</span>
              </div>
            </div>

            <div className="settings-field">
              <label>Email Address</label>
              <div className="readonly-input-wrapper">
                <input type="email" value={email} readOnly />
                <span className="readonly-badge">Read-only</span>
              </div>
            </div>

            <div className="settings-field">
              <label>Phone Number (Web Dialer Callback)</label>
              <div className="readonly-input-wrapper">
                <input type="text" value={phone} readOnly />
                <span className="readonly-badge">Read-only</span>
              </div>
            </div>
          </div>
        </div>

        {/* Password Reset Section */}
        <div className="settings-card">
          <div className="settings-card-header">
            <div className="settings-card-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <div>
              <h2>Security &amp; Password Update</h2>
              <p>Manage your account security and reset your password.</p>
            </div>
          </div>

          <form onSubmit={handlePasswordSubmit} className="settings-password-form">
            <div className="settings-fields-grid" style={{ marginBottom: '1.25rem' }}>
              <div className="settings-field">
                <label>Current Password</label>
                <input
                  type="password"
                  name="current"
                  value={passwordForm.current}
                  onChange={handlePasswordChange}
                  placeholder="Enter current password"
                  required
                />
              </div>

              <div className="settings-field">
                <label>New Password</label>
                <input
                  type="password"
                  name="new"
                  value={passwordForm.new}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                  required
                />
              </div>

              <div className="settings-field">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  name="confirm"
                  value={passwordForm.confirm}
                  onChange={handlePasswordChange}
                  placeholder="Confirm new password"
                  required
                />
              </div>
            </div>

            <div className="password-submit-wrapper">
              <button type="submit" className="settings-primary-btn">
                Update Password
              </button>
              {passwordMsg && (
                <span className={`password-msg ${passwordMsg.includes('success') ? 'success' : 'error'}`}>
                  {passwordMsg}
                </span>
              )}
            </div>
          </form>
        </div>

      </div>
    </div>
  );
}
